import React, { useState, useContext, useCallback } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { TranslationContext, StyleTypeContext } from "../../../umbilical_tools";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { DateTime } from "luxon";
import { DatepickerField } from "../../forms/inputGroup/helpers";
import { Formik, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { flightSearchFormUpdate, singleFlightSearch } from "../../../actions/search/flights_search";
import { getFieldsOptions, validateFlightSearch } from "./helpers";
import PropTypes from "prop-types";
import { NoScrollContext } from "../../../context";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { PaxPicker } from "../widgets";
import { AirportAutoCompleteField, ReactSelectField } from "../../forms/inputGroup/helpers";
import jssVariables from "../../../jss_setup";
import { useApoAuth } from "../../../hooks";
import { SearchButton } from "../../common/Button";

const styles = createUseStyles({
  flightsSearchForm: (props) => ({
    padding: [0, jssVariables.normal_gap],
    paddingBottom: jssVariables.normal_gap,
    border: `${props.borderSize} solid ${props.baseColor}`,
    borderRadius: props.borderRadius,
    display: "grid",
    gridGap: jssVariables.half_gap,
    background: props.background,
    ...(props?.customStyles?.flightsSearchForm ?? {}),
  }),
  fieldsContainer: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ...(props?.customStyles?.fieldsContainer ?? {}),
  }),
  basicFields: (props) => ({
    gridGap: jssVariables.half_gap,
    display: "grid",
    ...(props?.customStyles?.basicFields ?? {}),
  }),
  extraFields: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: jssVariables.half_gap,
    ...(props?.customStyles?.extraFields ?? {}),
  }),
  destMobile: {},
  destDesktop: {
    display: "none",
  },
  col2Top: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.half_gap,
    ...(props?.customStyles?.col2Top ?? {}),
  }),
  col2Bottom: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.half_gap,
    ...(props?.customStyles?.col2Bottom ?? {}),
  }),
  stopsCompMobile: (props) => ({
    ...(props?.customStyles?.stopsCompMobile ?? {}),
  }),
  stopsCompDesktop: (props) => ({
    display: "none",
    ...(props?.customStyles?.stopsCompDesktop ?? {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ...(props?.customStyles?.searchBtnContr ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    flightsSearchForm: (props) => ({
      gridTemplateColumns: "1fr max-content",
      alignItems: "end",
      padding: 0,
      ...(props?.customStyles?.flightsSearchForm?.smallscreen ?? {}),
    }),
    fieldsContainer: (props) => ({
      ...(props?.customStyles?.fieldsContainer?.smallscreen ?? {}),
    }),
    basicFields: (props) => ({
      display: "grid",
      gridTemplateColumns: "9rem 1fr 1fr",
      ...(props?.customStyles?.basicFields?.smallscreen ?? {}),
    }),
    extraFields: (props) => ({
      gridTemplateColumns: "9rem 13rem 14rem 1fr",
      ...(props?.customStyles?.extraFields?.smallscreen ?? {}),
    }),
    destMobile: {
      display: "none",
    },
    destDesktop: {
      display: "block",
    },
    col2Top: (props) => ({
      gridTemplateColumns: "1fr",
      ...(props?.customStyles?.col2Top?.smallscreen ?? {}),
    }),
    col2Bottom: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridGap: jssVariables.half_gap,
      ...(props?.customStyles?.col2Bottom?.smallscreen ?? {}),
    }),
    stopsCompMobile: (props) => ({
      display: "none",
      ...(props?.customStyles?.stopsCompMobile?.smallscreen ?? {}),
    }),
    stopsCompDesktop: (props) => ({
      display: "block",
      ...(props?.customStyles?.stopsCompDesktop?.smallscreen ?? {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      ...(props?.customStyles?.searchBtnContr?.smallscreen ?? {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    flightsSearchForm: (props) => ({
      ...(props?.customStyles?.flightsSearchForm?.bigscreen ?? {}),
    }),
  },
});
const FlightsSearchForm = ({ setShowPaxPickerModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const dispatch = useDispatch();
  const history = useHistory();

  const { source_entity, market_type, website_api_key } = useApoAuth();

  const { toggleNoScroll } = useContext(NoScrollContext);

  const { t } = useContext(TranslationContext);

  const search_form = useSelector((state) => state.FlightSearchForm);

  const { outbound_departure, isRoundTrip } = search_form;
  const [roundTrip, setRoundTrip] = useState(isRoundTrip);

  const customStyles = cStyler({
    type,
    brand,
    objName: "lpFlightsSearchFormStyles",
  });

  const classes = styles({ customStyles });

  const onFormUpdate = useCallback(
    ({ form }) => dispatch(flightSearchFormUpdate({ form })),
    [dispatch],
  );

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  const onFormSearch = useCallback(async () => {
    dispatch(
      singleFlightSearch({
        history,
        source_entity,
        market_type,
        website_api_key,
      }),
    );
  }, [dispatch, history, market_type, source_entity, website_api_key]);

  return (
    <Formik
      validateOnBlur={false}
      validateOnMount={false}
      initialValues={{
        origin: { origin_meta: _.get(search_form, "origin_meta", []) },
        destination: {
          destination_meta: _.get(search_form, "destination_meta", []),
        },
        datesArray: [outbound_departure, null],
        roundTrip: { value: false, label: t("oneWay") },
        baggage_only: { value: false, label: t("noBaggagePreference") },
        stops: { value: "AllFlight", label: t("anyFlights") },
        flight_class: { value: "All", label: t("noClassPreference") },
      }}
      validate={async (values) => {
        if (values.roundTrip !== roundTrip) setRoundTrip(values.roundTrip.value);
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);
          let outboundDeparture = values.datesArray;
          if (Array.isArray(values.datesArray) && values.datesArray[0]) {
            outboundDeparture = values.datesArray[0];
          }
          let form: {
            adults: any;
            children: any;
            children_ages: any;
            origin: { origin_meta: any };
            destination: { destination_meta: any };
            outbound_departure: any;
            isRoundTrip: boolean;
            baggage_only: boolean;
            stops: string;
            flight_class: string;
            inbound_departure?: any;
          } = {
            adults: search_form.adults,
            children: search_form.children,
            children_ages: search_form.children_ages,
            origin: values.origin,
            destination: values.destination,
            outbound_departure: outboundDeparture,
            isRoundTrip: values.roundTrip.value,
            baggage_only: values.baggage_only.value,
            stops: values.stops.value,
            flight_class: values.flight_class.value,
          };

          if (form.isRoundTrip) {
            form = {
              ...form,
              inbound_departure: values.datesArray[1],
            };
          }
          if (validateFlightSearch(form)) {
            await onFormUpdate({ form });
            onFormSearch();
          } else {
            setIsSubmitting(false);
          }
        }
      }}
    >
      <Form className={classes.flightsSearchForm}>
        <div className={classes.fieldsContainer}>
          <div className={classes.basicFields}>
            <div className={classes.col2Top}>
              <ReactSelectField
                id="roundTrip"
                name="roundTrip"
                options={getFieldsOptions({ name: "roundTrip", t })}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <div className={classes.stopsCompMobile}>
                <ReactSelectField
                  name="stops"
                  options={getFieldsOptions({ name: "stops", t })}
                  customStyles={_.get(customStyles, "inputGroupField", {})}
                />
              </div>
            </div>
            <AirportAutoCompleteField
              id="origin"
              name="origin"
              placeholder={`${t("originAirport")}`}
              withErrors={false}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <span className={classes.destDesktop}>
              <AirportAutoCompleteField
                id="destination"
                name="destination"
                placeholder={`${t("destination")} ${t("airport")}`}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </span>
            <span className={classes.destMobile}>
              <AirportAutoCompleteField
                name="destination"
                placeholder={`${t("destination")} ${t("airport")}`}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </span>
          </div>

          <div className={classes.extraFields}>
            <div className={classes.stopsCompDesktop}>
              <ReactSelectField
                id="stops"
                name="stops"
                options={getFieldsOptions({ name: "stops", t })}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </div>
            <PaxPicker
              customStyles={_.get(customStyles, "inputGroupField", {})}
              adults={search_form.adults}
              childs={search_form.children}
              children_ages={search_form.children_ages}
              // infantsOnLap={search_form.infantsOnLap}
              // infantsInSeat={search_form.infantsInSeat}
              onChange={(values) => {
                onFormUpdate(values);
              }}
              onClick={onClickPax}
              product_type="FL"
              onClickPax={onClickPax}
              showLabel={false}
            />
            <DatepickerField
              id="datesArray"
              name="datesArray"
              selectsRange={roundTrip}
              dateFormat="MMM d, yyyy"
              minDate={DateTime.now().toISODate()}
              maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
              placeholder={roundTrip ? "Mon DD, YYYY - Mon DD, YYYY" : "Mon DD, YYYY"}
              withPortal={true}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
            <div className={classes.col2Bottom}>
              <ReactSelectField
                id="baggage_only"
                name="baggage_only"
                options={getFieldsOptions({ name: "withBaggage", t })}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              <ReactSelectField
                id="flight_class"
                name="flight_class"
                options={getFieldsOptions({ name: "flightClass", t })}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
            </div>
          </div>
        </div>
        <div className={classes.searchBtnContr}>
          <SearchButton
            id="flightSearchBtn"
            text={t("search")}
            loading={isSubmitting}
            customStyles={_.get(customStyles, "searchBtn", {})}
          />
        </div>
      </Form>
    </Formik>
  );
};
FlightsSearchForm.defaultProps = {};
FlightsSearchForm.propTypes = {
  setShowPaxPickerModal: PropTypes.func.isRequired,
};
export default FlightsSearchForm;
